/**
 * Survey Readiness Dashboard - Sprint 7 + Sprint 10 Branding
 * Shows compliance summaries and allows ZIP evidence pack exports
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { surveyAPI, clientsAPI } from "../lib/api";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ClipboardCheck,
  RefreshCw,
  Download,
  Users,
  UserCheck,
  AlertTriangle,
  FileText,
  CheckCircle,
  XCircle,
  Calendar,
  Building2,
} from "lucide-react";

// Tile component for dashboard metrics
const ComplianceTile = ({ title, icon: Icon, metrics, status, onClick, testId }) => {
  const statusColors = {
    ok: "border-green-200 bg-green-50/50",
    warning: "border-yellow-200 bg-yellow-50/50",
    critical: "border-red-200 bg-red-50/50",
  };
  
  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-shadow ${statusColors[status] || ""}`}
      onClick={onClick}
      data-testid={testId}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
          <div className={`p-2 rounded-lg ${status === "ok" ? "bg-green-100" : status === "critical" ? "bg-red-100" : "bg-yellow-100"}`}>
            <Icon className={`h-4 w-4 ${status === "ok" ? "text-green-600" : status === "critical" ? "text-red-600" : "text-yellow-600"}`} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {metrics.map((metric, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <span className="text-xs text-slate-500">{metric.label}</span>
              <span className={`text-sm font-semibold ${metric.highlight ? (metric.value > 0 ? "text-red-600" : "text-green-600") : "text-slate-900"}`}>
                {metric.value}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Aging bucket bar visualization
const AgingBuckets = ({ buckets }) => {
  const bucketColors = {
    "0-7 days": "bg-green-500",
    "8-14 days": "bg-yellow-400",
    "15-30 days": "bg-orange-400",
    "31-60 days": "bg-red-400",
    "60+ days": "bg-red-600",
  };
  
  const total = Object.values(buckets || {}).reduce((a, b) => a + b, 0);
  if (total === 0) return <p className="text-sm text-slate-500">No open exceptions</p>;
  
  return (
    <div className="space-y-2">
      {Object.entries(buckets || {}).map(([label, count]) => (
        <div key={label} className="flex items-center gap-2">
          <span className="text-xs text-slate-500 w-20">{label}</span>
          <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full ${bucketColors[label] || "bg-slate-400"} rounded-full`}
              style={{ width: `${(count / total) * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium text-slate-700 w-8 text-right">{count}</span>
        </div>
      ))}
    </div>
  );
};

const SurveyDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState(null); // 'client' or 'agency'
  const [exporting, setExporting] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clients, setClients] = useState([]);
  
  // Check if user can export (Admin or ComplianceManager)
  const canExport = user?.role === "Admin" || user?.role === "ComplianceManager";

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await surveyAPI.getDashboard();
      setDashboard(res.data);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to load dashboard";
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await clientsAPI.list({ status_filter: "Active" });
      setClients(res.data.clients || []);
    } catch (error) {
      // Silent fail - clients list is optional
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchClients();
  }, [fetchDashboard, fetchClients]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboard();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const openExportDialog = (type) => {
    setExportType(type);
    setSelectedClientId("");
    setDateFrom("");
    setDateTo("");
    setExportDialogOpen(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      let response;
      let filename;
      
      if (exportType === "client") {
        if (!selectedClientId) {
          toast.error("Please select a client");
          setExporting(false);
          return;
        }
        response = await surveyAPI.exportClientPack(selectedClientId, dateFrom || null, dateTo || null);
        filename = `client_pack_${selectedClientId.slice(0, 8)}_${new Date().toISOString().slice(0,10)}.zip`;
      } else {
        response = await surveyAPI.exportAgencyPack(dateFrom || null, dateTo || null);
        filename = `agency_pack_${new Date().toISOString().slice(0,10)}.zip`;
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${exportType === "client" ? "Client" : "Agency"} evidence pack downloaded`);
      setExportDialogOpen(false);
    } catch (error) {
      const msg = error.response?.data?.detail || "Export failed";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  // Calculate status for each tile
  const getClientStatus = () => {
    if (!dashboard?.client_compliance) return "ok";
    return dashboard.client_compliance.clients_missing_docs_count > 0 ? "warning" : "ok";
  };

  const getPersonnelStatus = () => {
    if (!dashboard?.personnel_compliance) return "ok";
    return dashboard.personnel_compliance.noncompliant_caregivers_count > 0 ? "warning" : "ok";
  };

  const getEvvStatus = () => {
    if (!dashboard?.evv_compliance) return "ok";
    const count = dashboard.evv_compliance.open_exceptions_count || 0;
    if (count === 0) return "ok";
    if (count > 10) return "critical";
    return "warning";
  };

  const getVisitStatus = () => {
    if (!dashboard?.visit_documentation) return "ok";
    const v = dashboard.visit_documentation;
    if (v.billable_rule_violations_count > 0 || v.completed_missing_notes_count > 5) return "critical";
    if (v.completed_missing_notes_count > 0) return "warning";
    return "ok";
  };

  if (loading) {
    return (
      <Layout title="Survey Readiness">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2 text-slate-600">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading survey dashboard...</span>
          </div>
        </div>
      </Layout>
    );
  }

  const cc = dashboard?.client_compliance || {};
  const pc = dashboard?.personnel_compliance || {};
  const ec = dashboard?.evv_compliance || {};
  const vd = dashboard?.visit_documentation || {};

  return (
    <Layout title="Survey Readiness">
      <div className="space-y-6" data-testid="survey-dashboard-page">
        {/* Actions Row */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-sm text-slate-500">
            Last updated: {dashboard?.generated_at ? new Date(dashboard.generated_at).toLocaleString() : "N/A"}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {canExport && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openExportDialog("client")}
                  data-testid="export-client-button"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Client Pack
                </Button>
                <Button
                  size="sm"
                  onClick={() => openExportDialog("agency")}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="export-agency-button"
                >
                  <Building2 className="h-4 w-4 mr-1" />
                  Agency Pack
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Compliance Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <ComplianceTile
            title="Client Compliance"
            icon={Users}
            status={getClientStatus()}
            testId="tile-client-compliance"
            metrics={[
              { label: "Active Clients", value: cc.active_clients_count || 0 },
              { label: "Missing Docs", value: cc.clients_missing_docs_count || 0, highlight: true },
            ]}
            onClick={() => navigate("/clients")}
          />
          
          <ComplianceTile
            title="Personnel Compliance"
            icon={UserCheck}
            status={getPersonnelStatus()}
            testId="tile-personnel-compliance"
            metrics={[
              { label: "Active Caregivers", value: pc.active_caregivers_count || 0 },
              { label: "Non-Compliant", value: pc.noncompliant_caregivers_count || 0, highlight: true },
              { label: "Expiring (30d)", value: pc.trainings_expiring_30_days_count || 0, highlight: true },
            ]}
            onClick={() => navigate("/training-matrix")}
          />
          
          <ComplianceTile
            title="EVV Compliance"
            icon={AlertTriangle}
            status={getEvvStatus()}
            testId="tile-evv-compliance"
            metrics={[
              { label: "Open Exceptions", value: ec.open_exceptions_count || 0, highlight: true },
            ]}
            onClick={() => navigate("/evv-exceptions")}
          />
          
          <ComplianceTile
            title="Visit Documentation"
            icon={FileText}
            status={getVisitStatus()}
            testId="tile-visit-documentation"
            metrics={[
              { label: "Missing Notes", value: vd.completed_missing_notes_count || 0, highlight: true },
              { label: "Billable Violations", value: vd.billable_rule_violations_count || 0, highlight: true },
              { label: "Not Billable", value: vd.completed_not_billable_count || 0 },
            ]}
            onClick={() => navigate("/schedule")}
          />
        </div>

        {/* Detail Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* EVV Aging Buckets */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center">
                <AlertTriangle className="h-4 w-4 mr-2 text-orange-500" />
                EVV Exception Aging
              </CardTitle>
              <CardDescription>Open exceptions by age</CardDescription>
            </CardHeader>
            <CardContent>
              <AgingBuckets buckets={ec.aging_buckets} />
            </CardContent>
          </Card>

          {/* Clients with Missing Docs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center">
                <Users className="h-4 w-4 mr-2 text-blue-500" />
                Clients with Missing Documents
              </CardTitle>
              <CardDescription>
                {(cc.clients_missing_docs_list || []).length} clients need attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(cc.clients_missing_docs_list || []).length === 0 ? (
                <div className="flex items-center justify-center py-4 text-green-600">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span>All clients have complete documents</span>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Client</TableHead>
                        <TableHead className="text-xs text-right">Complete</TableHead>
                        <TableHead className="text-xs">Missing</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(cc.clients_missing_docs_list || []).slice(0, 10).map((c) => (
                        <TableRow 
                          key={c.client_id} 
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => navigate(`/clients/${c.client_id}`)}
                        >
                          <TableCell className="text-xs font-medium">{c.name}</TableCell>
                          <TableCell className="text-xs text-right">{c.percent_complete}%</TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="text-red-600 border-red-200 text-xs">
                              {(c.missing || []).length + (c.expired || []).length} docs
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Non-Compliant Personnel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center">
                <UserCheck className="h-4 w-4 mr-2 text-purple-500" />
                Non-Compliant Personnel
              </CardTitle>
              <CardDescription>
                {(pc.noncompliant_list || []).length} caregivers need training updates
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(pc.noncompliant_list || []).length === 0 ? (
                <div className="flex items-center justify-center py-4 text-green-600">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span>All caregivers are compliant</span>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Missing</TableHead>
                        <TableHead className="text-xs">Expired</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(pc.noncompliant_list || []).slice(0, 10).map((p) => (
                        <TableRow 
                          key={p.personnel_id}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => navigate(`/personnel/${p.personnel_id}`)}
                        >
                          <TableCell className="text-xs font-medium">{p.name}</TableCell>
                          <TableCell className="text-xs">
                            {(p.missing || []).length > 0 && (
                              <Badge variant="outline" className="text-orange-600 border-orange-200 text-xs">
                                {(p.missing || []).length}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {(p.expired || []).length > 0 && (
                              <Badge variant="outline" className="text-red-600 border-red-200 text-xs">
                                {(p.expired || []).length}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Visits Missing Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center">
                <FileText className="h-4 w-4 mr-2 text-red-500" />
                Completed Visits Missing Notes
              </CardTitle>
              <CardDescription>
                {(vd.completed_missing_notes_list || []).length} visits need documentation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(vd.completed_missing_notes_list || []).length === 0 ? (
                <div className="flex items-center justify-center py-4 text-green-600">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span>All completed visits have notes</span>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Client</TableHead>
                        <TableHead className="text-xs">Caregiver</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(vd.completed_missing_notes_list || []).slice(0, 10).map((v) => (
                        <TableRow 
                          key={v.visit_id}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => navigate(`/visits/${v.visit_id}`)}
                        >
                          <TableCell className="text-xs">{v.date}</TableCell>
                          <TableCell className="text-xs font-medium">{v.client_name}</TableCell>
                          <TableCell className="text-xs">{v.caregiver_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>
              {exportType === "client" ? "Export Client Evidence Pack" : "Export Agency Evidence Pack"}
            </DialogTitle>
            <DialogDescription>
              {exportType === "client" 
                ? "Generate a ZIP file containing all compliance documents for a specific client."
                : "Generate a ZIP file containing agency-wide compliance reports."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {exportType === "client" && (
              <div className="space-y-2">
                <Label htmlFor="client-select">Select Client</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger id="client-select" data-testid="client-select">
                    <SelectValue placeholder="Choose a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.last_name}, {c.first_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date-from">From Date (optional)</Label>
                <Input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="date-from-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date-to">To Date (optional)</Label>
                <Input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="date-to-input"
                />
              </div>
            </div>
            
            <p className="text-xs text-slate-500">
              Leave dates empty to use the default range (last 90 days).
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)} disabled={exporting}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport} 
              disabled={exporting || (exportType === "client" && !selectedClientId)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-export-button"
            >
              {exporting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  Download ZIP
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
};

export default SurveyDashboardPage;
